import { useCallback, useEffect, useRef, useState } from 'react';
import { CpmButton, CpmFilterDropdown } from '../../../components/design-system';
import {
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
    listStates,
    listStoreBrands,
    updateCatalogStoreBrands,
} from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    FormPhase,
    PlaceLoadState,
    StoreBrandListRow,
} from '../../../types/reportsAdmin.types';
import { REPORTS_COPY } from '../copy';
import { decodeLicence, licenceOptionsForStates, stateName } from '../helpers';
import {
    ReportsAlert,
    ReportsDialog,
    ReportsDialogBody,
    ReportsDialogFooter,
    ReportsDialogHeader,
    ReportsSkeletonList,
    ReportsWait,
} from '../components/ReportsDialog';
import AppliedFilterChips, { type AppliedChip } from '../components/AppliedFilterChips';
import FilterToggleChip from '../components/FilterToggleChip';
import SearchField from '../components/SearchField';
import StoreBrandList from '../components/StoreBrandList';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

interface StoreBrandsForReportModalProps {
    reportID: string;
    onClose: () => void;
    onSaved: (brandsOn: number) => void;
}

export default function StoreBrandsForReportModal({
    reportID,
    onClose,
    onSaved,
}: StoreBrandsForReportModalProps) {
    const [search, setSearch] = useState('');
    const debounced = useDebouncedValue(search, 300);
    const [alreadyOn, setAlreadyOn] = useState(true);
    const [states, setStates] = useState<string[]>([]);
    const [licences, setLicences] = useState<string[]>([]);
    const [stateDocs, setStateDocs] = useState<CpmState[]>([]);
    const [statesError, setStatesError] = useState(false);
    const [statesLoading, setStatesLoading] = useState(false);
    const [rows, setRows] = useState<StoreBrandListRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [place, setPlace] = useState<PlaceLoadState>('loading');
    const [listBusy, setListBusy] = useState(false);
    const readyOnce = useRef(false);
    const [initialOn, setInitialOn] = useState<Set<string>>(new Set());
    const [ticked, setTicked] = useState<Set<string>>(new Set());
    const [phase, setPhase] = useState<FormPhase>('idle');
    const [error, setError] = useState('');
    const [receipt, setReceipt] = useState('');
    const [moreBusy, setMoreBusy] = useState(false);
    const [moreFailed, setMoreFailed] = useState(false);

    const locked = phase === 'saving';
    const licenceMeta = licenceOptionsForStates(stateDocs, states);

    const loadStates = useCallback(async () => {
        setStatesLoading(true);
        setStatesError(false);
        try {
            setStateDocs(await listStates());
        } catch {
            setStatesError(true);
        } finally {
            setStatesLoading(false);
        }
    }, []);

    const load = useCallback(
        async (nextPage: number, append: boolean) => {
            if (!append) {
                setMoreFailed(false);
                if (readyOnce.current) setListBusy(true);
                else setPlace('loading');
            } else {
                setMoreBusy(true);
                setMoreFailed(false);
            }
            try {
                const data = await listStoreBrands({
                    reportID,
                    alreadyOn,
                    search: debounced || undefined,
                    states: states.length ? states : undefined,
                    licenceTypes: licences.length ? licences.map(decodeLicence) : undefined,
                    page: nextPage,
                    limit: 25,
                });
                setRows((prev) => (append ? [...prev, ...data.storeBrands] : data.storeBrands));
                setTotal(data.total);
                setPage(nextPage);
                const apply = (prev: Set<string>, seed: boolean) => {
                    const next = seed ? new Set<string>() : new Set(prev);
                    data.storeBrands.forEach((row) => {
                        if (row.alreadyOn) next.add(row.storeBrandId);
                    });
                    return next;
                };
                setInitialOn((prev) => apply(prev, !append));
                setTicked((prev) => apply(prev, !append));
                readyOnce.current = true;
                setPlace('ready');
            } catch (err) {
                if (append) setMoreFailed(true);
                else if (!readyOnce.current) {
                    setError(getReportsAdminErrorMessage(err));
                    setPlace('error');
                }
            } finally {
                setListBusy(false);
                setMoreBusy(false);
            }
        },
        [alreadyOn, debounced, licences, reportID, states]
    );

    useEffect(() => {
        void loadStates();
    }, [loadStates]);

    useEffect(() => {
        void load(1, false);
    }, [load]);

    const toggle = (id: string) => {
        if (locked) return;
        setReceipt('');
        setTicked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const onStoreBrandIds = rows
        .filter((row) => ticked.has(row.storeBrandId) && !initialOn.has(row.storeBrandId))
        .map((row) => row.storeBrandId);
    const offStoreBrandIds = rows
        .filter((row) => !ticked.has(row.storeBrandId) && initialOn.has(row.storeBrandId))
        .map((row) => row.storeBrandId);
    const dirty = onStoreBrandIds.length + offStoreBrandIds.length > 0;

    const persist = async () => {
        if (!dirty) return;
        setPhase('saving');
        setError('');
        try {
            const result = await updateCatalogStoreBrands({
                reportID,
                onStoreBrandIds,
                offStoreBrandIds,
            });
            setInitialOn(new Set(ticked));
            setReceipt(
                `${REPORTS_COPY.SAVED_ON_BRANDS} ${result.brandsOn} store brands. Turned on ${result.turnedOn} · turned off ${result.turnedOff}.`
            );
            setPhase('done');
            onSaved(result.brandsOn);
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    const chips: AppliedChip[] = [
        ...states.map((state) => ({
            key: `st-${state}`,
            label: state,
            onClear: () => setStates((prev) => prev.filter((item) => item !== state)),
        })),
        ...licences.map((licence) => ({
            key: `lic-${licence}`,
            label: licenceMeta.options.find((item) => item.value === licence)?.label ?? licence,
            onClear: () => setLicences((prev) => prev.filter((item) => item !== licence)),
        })),
    ];

    const hasFilters = Boolean(debounced) || states.length > 0 || licences.length > 0 || alreadyOn;

    return (
        <ReportsDialog
            open
            width={740}
            zIndex={400}
            onClose={locked ? () => undefined : onClose}
            closeOnBackdrop={!locked}
            closeOnEscape={!locked}
        >
            <ReportsDialogHeader
                kicker={REPORTS_COPY.ON_FOR_STORE_BRANDS}
                title="Store brands this report is on for"
                subtitle="Tick a store brand to turn this report on for it, untick to turn it off. Store brands that already have it open ticked. No template changes — a store brand is never linked to a template."
                onClose={locked ? undefined : onClose}
            />
            <ReportsDialogBody pad="picker">
                {place === 'loading' ? (
                    <div>
                        <div style={{ paddingBottom: 16 }}>
                            <ReportsWait label="Loading store brands, and which of them already have this on…" />
                        </div>
                        <div className="flex" style={{ gap: 9, paddingBottom: 14 }}>
                            <div className="cpm-sk" style={{ height: 36, width: 300 }} />
                            <div className="cpm-sk" style={{ height: 36, width: 108 }} />
                        </div>
                        <ReportsSkeletonList />
                    </div>
                ) : null}
                {place === 'error' ? (
                    <ReportsAlert
                        tone="danger"
                        title="Couldn't load the store brands"
                        body="The store brand list did not come back, so this is not an empty system. Nothing was written, and the Catalogue list behind this dialog was not part of this request."
                        reason={reportsAdminErrorReason(error)}
                        action={
                            <CpmButton
                                variant="outline"
                                size="sm"
                                onClick={() => void load(1, false)}
                            >
                                Retry
                            </CpmButton>
                        }
                    />
                ) : null}
                {place === 'ready' ? (
                    <div className="flex flex-col" style={{ gap: 12 }}>
                        {error ? (
                            <ReportsAlert
                                tone="danger"
                                title="Couldn't save the store brands for this report"
                                body="No store brand changed and every tick you made is still here. Retry when you are ready."
                                reason={reportsAdminErrorReason(
                                    error,
                                    'No store brand changed and every tick you made is still here.'
                                )}
                                action={
                                    <CpmButton
                                        variant="outline"
                                        size="sm"
                                        onClick={() => void persist()}
                                    >
                                        {REPORTS_COPY.RETRY_THE_SAVE}
                                    </CpmButton>
                                }
                            />
                        ) : null}
                        {receipt ? (
                            <ReportsAlert
                                tone="success"
                                title={REPORTS_COPY.SAVED}
                                body={`${receipt} Templates are untouched: this adds the report to no template and removes it from none.`}
                            />
                        ) : null}
                        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                            <SearchField
                                value={search}
                                onChange={setSearch}
                                placeholder="Search a store brand by name"
                                disabled={locked}
                                width={300}
                                size="sm"
                            />
                            <CpmFilterDropdown
                                label="State"
                                triggerVariant="chip"
                                portal
                                disabled={locked}
                                menuMinWidth={286}
                                loading={statesLoading}
                                loadingLabel={REPORTS_COPY.LOADING_STATES}
                                failed={statesError}
                                failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                                failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                                onRetry={() => void loadStates()}
                                options={stateDocs
                                    .map(stateName)
                                    .filter(Boolean)
                                    .map((name) => ({ value: name, label: name }))}
                                selected={states}
                                onChange={setStates}
                            />
                            <CpmFilterDropdown
                                label="Licence"
                                triggerVariant="chip"
                                portal
                                disabled={locked}
                                options={licenceMeta.options}
                                selected={licences}
                                onChange={setLicences}
                            />
                            <FilterToggleChip
                                label={REPORTS_COPY.ALREADY_ON_FILTER}
                                on={alreadyOn}
                                disabled={locked}
                                onClick={() => setAlreadyOn((v) => !v)}
                            />
                        </div>
                        {licenceMeta.emptyNote && states.length > 0 ? (
                            <p
                                className="m-0"
                                style={{ fontSize: 12, color: 'var(--cpm-text-secondary)' }}
                            >
                                {REPORTS_COPY.LICENCE_EMPTY}
                            </p>
                        ) : null}
                        <AppliedFilterChips
                            chips={chips}
                            onClearAll={() => {
                                setStates([]);
                                setLicences([]);
                            }}
                        />
                        {listBusy ? (
                            <div>
                                <div style={{ paddingBottom: 12 }}>
                                    <ReportsWait label={REPORTS_COPY.LOADING_BRANDS} />
                                </div>
                                <ReportsSkeletonList rows={3} />
                            </div>
                        ) : (
                            <StoreBrandList
                                rows={rows}
                                total={total}
                                hasMore={rows.length < total}
                                loadingMore={moreBusy}
                                moreFailed={moreFailed}
                                onLoadMore={() => void load(page + 1, true)}
                                selectedIds={Array.from(ticked)}
                                onToggle={toggle}
                                showTicks
                                alreadyOnMode
                                searchActive={Boolean(debounced) || states.length > 0}
                                filteredEmpty={place === 'ready' && total === 0 && hasFilters}
                                onClearFilters={() => {
                                    setSearch('');
                                    setStates([]);
                                    setLicences([]);
                                    setAlreadyOn(false);
                                }}
                            />
                        )}
                    </div>
                ) : null}
            </ReportsDialogBody>
            <ReportsDialogFooter
                note="Store brands only. Templates are untouched."
                saving={locked}
                savingLabel="Saving which store brands have this report on. The write has not finished — do not fire it again."
                onCancel={onClose}
                primaryLabel={REPORTS_COPY.SAVE}
                onPrimary={() => void persist()}
                primaryDisabled={place !== 'ready' || listBusy || !dirty}
            />
        </ReportsDialog>
    );
}
